<script lang="ts">
export enum ButtonVariant {
  Primary,
  Secondary,
  Tertiary,
  Gold,
  Ai,
}
</script>

<script setup lang="ts">
import { iconProperty } from "./Icon.vue";
import ClickableComponent from "./ClickableComponent.vue";
import { useAttrs } from "vue";
import { useScreenSize } from "@/composables/useScreenSize";

const Icon = useAsyncComponent(() => import("@/components/basics/Icon.vue"));
const LoadingSmall = useAsyncComponent(() => import("@/components/LoadingSmall.vue"));

const props = defineProps({
  // Type is defined inside iconProperty.
  // eslint-disable-next-line vue/require-prop-types
  icon: { ...iconProperty, required: false },
  iconOnRight: Boolean,
  variant: {
    type: Number as PropType<ButtonVariant>,
    default: () => ButtonVariant.Primary,
  },

  /** @deprecated Use isLoading instead */
  loading: {
    type: Boolean,
    default: () => false,
  },
  isLoading: {
    type: Boolean,
    default: () => false,
  },
  loadingText: {
    type: String,
    default: () => "",
  },
  isDisabled: Boolean,
  isFullWidth: Boolean,
  /** Renders full-width only on phone-sized screens; the component tracks the breakpoint itself. */
  mobileFullWidth: Boolean,
  isGrowable: Boolean,
  /** Drops the default `min-width` so the button may shrink to fit a cramped row (e.g. a card footer next to an actions menu). */
  isShrinkable: Boolean,
});

const attrs = useAttrs();
const { isPhone } = useScreenSize();

const isFullWidthEffective = computed(() => props.isFullWidth || (props.mobileFullWidth && isPhone.value));

const isLoadingInProgress = computed(() => props.loading || props.isLoading); // Temp - remove when props.loading will not be used anywhere
// Merges the isDisabled prop with a raw `disabled` attribute some callers pass through instead
const isDisabledEffective = computed(() => props.isDisabled || isLoadingInProgress.value || Boolean(attrs.disabled));

const cssClasses = computed(() => {
  return [
    attrs.class,
    {
      "btn-new": true,
      "btn-new--primary": props.variant === ButtonVariant.Primary,
      "btn-new--secondary": props.variant === ButtonVariant.Secondary,
      "btn-new--tertiary": props.variant === ButtonVariant.Tertiary,
      "btn-new--theme-gold": props.variant === ButtonVariant.Gold,
      "btn-new--ai": props.variant === ButtonVariant.Ai,
      "btn-new--with-loader": isLoadingInProgress.value,
      "btn-new--is-disabled": isDisabledEffective.value,
      "btn-new--is-full-width": isFullWidthEffective.value,
      "btn-new--is-growable": props.isGrowable === true,
      "btn-new--is-shrinkable": props.isShrinkable === true,
      "btn-new--icon-on-right": props.iconOnRight,
    },
  ];
});

const attrsWithoutClass = computed(() => {
  const { class: _, disabled: __, ...rest } = attrs;
  return rest;
});

const loaderColor = computed(() => {
  if (props.variant === ButtonVariant.Secondary) {
    return "var(--gray950)";
  } else {
    return "var(--green50)";
  }
});
</script>

<template>
  <ClickableComponent v-bind="attrsWithoutClass" :class="cssClasses" :disabled="isDisabledEffective || undefined" :aria-busy="isLoadingInProgress || undefined" :aria-disabled="isDisabledEffective || undefined">
    <div v-if="isLoadingInProgress" class="btn-new__loader-wrapper">
      <LoadingSmall :color="loaderColor" />
    </div>

    <Icon v-if="icon !== undefined && !isLoadingInProgress" :icon="icon" class="btn-new__icon" />

    <template v-if="isLoadingInProgress && loadingText">
      {{ loadingText }}
    </template>
    <slot v-else></slot>
  </ClickableComponent>
</template>

<style lang="scss" scoped>
.btn-new {
  --bgColor: var(--green500);
  --color: var(--white);

  height: var(--controlHeight);
  min-width: 110px;
  padding: 0 var(--spacing3x);
  background-color: var(--bgColor);
  transition:
    background-color var(--uiTransitionDuration) ease,
    color var(--uiTransitionDuration) ease;
  cursor: pointer;
  border-radius: var(--controlRadius);
  font: var(--controlFontSize) / 1.4 var(--font-family);
  color: var(--color);
  text-decoration: none;
  isolation: isolate;
  flex-shrink: 0;
  border: 0;
  @include mixins.outline;

  /* stylelint-disable-next-line selector-disallowed-list -- Element Plus renders slot content in its own <span> */
  &:deep(> span) {
    gap: calc(var(--spacing3x) / 2);
  }
  &:hover {
    text-decoration: none;
    @include mixins.controlHoverStrong(--bgColor);
  }

  &--primary {
    --bgColor: var(--uiButtonBackgroundColorPrimary);
    --color: var(--uiButtonTextColorPrimary);
  }
  &--secondary {
    --bgColor: var(--uiButtonBackgroundColorSecondary);
    --color: var(--uiButtonTextColorSecondary);

    &:hover {
      @include mixins.controlHoverSubtle(--bgColor);
    }
  }
  &--tertiary {
    --bgColor: var(--uiButtonBackgroundColorTertiary);
    --color: var(--uiButtonTextColorTertiary);

    &:hover {
      @include mixins.controlHoverSubtle(--bgColor);
    }
  }
  &--theme-gold {
    --bgColor: var(--gold300);
    --color: var(--gold700);

    &:hover {
      @include mixins.controlHoverSubtle(--bgColor);
    }
  }

  &--ai {
    position: relative;
    background-image: var(--aiGradient);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: var(--controlRadius);
      background-color: rgba(0, 0, 0, var(--uiHoverStrongStrength));
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      &::before {
        opacity: 1;
      }
    }

    &.btn-new--is-disabled {
      background-image: none;
    }

    /* stylelint-disable-next-line selector-disallowed-list -- Element Plus renders slot content in its own <span> */
    &:deep(> span) {
      position: relative; // becouse of the hover effect
    }
  }

  &--with-loader {
    padding: 0 var(--spacing1x);
  }
  &--is-disabled {
    --bgColor: var(--gray300);
    pointer-events: none;
    cursor: not-allowed;
  }
  &--is-full-width {
    width: 100%;
  }
  &--is-growable {
    flex-grow: 1;
  }
  &--is-shrinkable {
    min-width: 0;
  }
  &--icon-on-right {
    .btn-new__icon {
      order: 2;
    }
  }
  &__icon {
    width: var(--controlIconSize);
    height: var(--controlIconSize);
  }
  &__loader-wrapper {
    position: relative;
    width: 12px;
    height: 12px;
  }

  @include mixins.buttonStateActive;
  @include mixins.buttonStateFocus;
  @include mixins.buttonStateFocusWithin;
}
</style>
